import { useSharedVoices } from '@/hooks/use-voices/use-voices';
import { addSharedVoice } from '@/services/voices-service/voices.service';
import { SharedVoice, Voice } from '@/services/voices-service/voices.service.types';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Alert, Drawer, Flex, Input, Select, Spin, Tag, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/button/button';
import styles from './voice-selector-drawer.module.scss';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ageLabel, categoryLabel, genderLabel } from './voice-labels';

// Language names follow the UI language. Polish writes them lower-case ('angielski'), so the
// first letter is raised to read as a label in a tag or a dropdown.
function getLanguageLabel(code: string): string {
  try {
    const name = new Intl.DisplayNames([i18n.language], { type: 'language' }).of(code);
    return name ? name.charAt(0).toLocaleUpperCase(i18n.language) + name.slice(1) : code;
  } catch {
    return code;
  }
}

// Common ElevenLabs library languages. Sourced from the values that appear
// in the public library — extending this list only affects the filter UI,
// not what's actually returned (the API drives that). Labels are derived from the code at
// render time (getLanguageLabel), so the list holds codes only.
const LANGUAGE_CODES = [
  'en',
  'es',
  'pl',
  'fr',
  'de',
  'it',
  'pt',
  'ru',
  'ar',
  'hi',
  'ja',
  'ko',
  'zh',
  'vi',
  'fil',
];

const GENDER_VALUES = ['male', 'female'] as const;

type VoiceSelectorDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (voice: Voice) => void;
  companyId: number | undefined;
  selectedVoiceId?: string | null;
  defaultSearch?: string;
  // ISO 639-1 prefix (e.g. "de"). When set, the language filter is pre-applied
  // so callers don't have to scroll through hundreds of unrelated voices.
  defaultLanguage?: string;
};

export function VoiceSelectorDrawer({
  open,
  onClose,
  onSelect,
  companyId,
  selectedVoiceId,
  defaultSearch,
  defaultLanguage,
}: VoiceSelectorDrawerProps) {
  const { t } = useTranslation(['voices', 'common']);
  const [search, setSearch] = useState<string | undefined>(defaultSearch);
  const [languageFilter, setLanguageFilter] = useState<string | undefined>(defaultLanguage);
  const [genderFilter, setGenderFilter] = useState<string | undefined>();
  const [isAdding, setIsAdding] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Hits ElevenLabs's shared/community library (hundreds of voices) via the
  // CRM's shared_voices proxy. Page size is intentionally generous because
  // the library is large and the current UX is a single-page list.
  const { voices, isPending, isError } = useSharedVoices(
    open ? companyId : undefined,
    search,
    100,
    { language: languageFilter, gender: genderFilter }
  );

  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  useEffect(() => {
    if (!open) {
      audioRef.current?.pause();
      setPlayingVoiceId(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch(defaultSearch);
      setLanguageFilter(defaultLanguage);
      setGenderFilter(undefined);
    }
  }, [open, defaultSearch, defaultLanguage]);

  const handlePlayPreview = (e: React.MouseEvent, voice: SharedVoice) => {
    e.stopPropagation();
    if (!voice.preview_url) return;

    if (playingVoiceId === voice.voice_id) {
      audioRef.current?.pause();
      setPlayingVoiceId(null);
      return;
    }

    audioRef.current?.pause();
    const audio = new Audio(voice.preview_url);
    audio.onended = () => setPlayingVoiceId(null);
    audio.play();
    audioRef.current = audio;
    setPlayingVoiceId(voice.voice_id);
  };

  const handleSelect = async (voice: SharedVoice) => {
    // Shared library voices need to be cloned into the workspace before they
    // can be used for TTS via the user's API key. The ID returned by the
    // search is the LIBRARY id and is NOT usable for TTS — only the workspace
    // id we get back from /v1/voices/add/{public_user_id}/{voice_id} is.
    if (!companyId) {
      message.error(t('voices:selector.errors.noCompany'));
      return;
    }
    if (!voice.public_owner_id) {
      // No public_owner_id means it's already a workspace voice (rare for the
      // shared-library endpoint, but possible). Pass through as-is.
      onSelect(voice as unknown as Voice);
      onClose();
      return;
    }

    setIsAdding(true);
    try {
      const result = await addSharedVoice({
        company_id: companyId,
        public_user_id: voice.public_owner_id,
        voice_id: voice.voice_id,
        new_name: voice.name,
      });
      if (!result?.voice_id) {
        // Earlier behavior fell through here and saved the LIBRARY voice_id,
        // which silently produced a broken row that 404s on every TTS call.
        // Refuse to save anything — keep the drawer open so the user can try
        // a different voice.
        message.error(t('voices:selector.errors.noVoiceId', { name: voice.name }));
        return;
      }
      onSelect({ ...(voice as unknown as Voice), voice_id: result.voice_id });
      onClose();
    } catch (e) {
      // Most common failure mode is a 422 from the CRM proxy carrying the
      // ElevenLabs error (quota exceeded, missing scope, etc.). Show the
      // actual error so the user can act on it instead of debugging from a
      // failing test call later.
      const errMsg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : t('voices:selector.errors.unknown'));
      message.error(t('voices:selector.errors.addFailed', { name: voice.name, error: errMsg }));
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Drawer
      title={t('voices:selector.title')}
      placement="right"
      width={640}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <div className={styles.searchBar}>
        <Input.Search
          placeholder={t('voices:selector.searchPlaceholder')}
          defaultValue={defaultSearch}
          onSearch={setSearch}
          allowClear
        />
        <Flex gap={8} style={{ marginTop: 8 }}>
          <Select
            showSearch
            placeholder={t('voices:filters.languagePlaceholder')}
            value={languageFilter}
            onChange={setLanguageFilter}
            allowClear
            style={{ flex: 1 }}
            options={LANGUAGE_CODES.map((code) => ({ label: getLanguageLabel(code), value: code }))}
            optionFilterProp="label"
          />
          <Select
            placeholder={t('voices:filters.genderPlaceholder')}
            value={genderFilter}
            onChange={setGenderFilter}
            allowClear
            style={{ width: 120 }}
            options={GENDER_VALUES.map((value) => ({
              label: t(`voices:gender.${value}`),
              value,
            }))}
          />
        </Flex>
      </div>

      {isAdding && (
        <Flex justify="center" align="center" gap={8} style={{ padding: 16, background: '#f5f5f5', borderRadius: 8, marginBottom: 16 }}>
          <Spin size="small" /> {t('voices:selector.adding')}
        </Flex>
      )}

      {isError && (
        <Alert
          type="warning"
          message={t('voices:selector.loadError')}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {isPending ? (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      ) : voices && voices.length > 0 ? (
        <div className={styles.grid}>
          {voices.map((voice) => {
            const isSelected = selectedVoiceId === voice.voice_id;
            return (
              <div
                key={voice.voice_id}
                className={`${styles.voiceCard} ${isSelected ? styles.voiceCardSelected : ''}`}
                onClick={() => handleSelect(voice)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSelect(voice); }}
              >
                <p className={styles.voiceName}>
                  {voice.name}
                  {isSelected && <CheckOutlined style={{ marginLeft: 6, color: '#52c41a' }} />}
                </p>
                {voice.description && (
                  <p className={styles.voiceDescription}>{voice.description}</p>
                )}
                <div className={styles.tags}>
                  {voice.language && <Tag color="blue">{getLanguageLabel(voice.language)}</Tag>}
                  {voice.gender && <Tag>{genderLabel(voice.gender)}</Tag>}
                  {voice.accent && <Tag>{voice.accent}</Tag>}
                  {voice.age && <Tag>{ageLabel(voice.age)}</Tag>}
                  {voice.use_case && <Tag>{voice.use_case}</Tag>}
                  {voice.category && <Tag color="default">{categoryLabel(voice.category)}</Tag>}
                </div>
                <div className={styles.voiceActions}>
                  {voice.preview_url && (
                    <Button
                      type="text"
                      size="small"
                      icon={playingVoiceId === voice.voice_id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                      onClick={(e) => handlePlayPreview(e, voice)}
                    >
                      {playingVoiceId === voice.voice_id
                        ? t('voices:actions.pause')
                        : t('voices:actions.preview')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !isError && (
          <div className={styles.emptyState}>
            <p>{t('voices:empty')}</p>
          </div>
        )
      )}
    </Drawer>
  );
}
